import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { StateService } from '@uirouter/angular';
import { FavoritesService } from '../favorites.service';
import { GfTitleService } from '../../../core/services/core.title.service';
import { NotificationService } from '../../../core/services/core.notification.service';
import { PageContextService } from '../../../core/services/core.page-context.service';

@Component({
    selector: 'favorite-button',
    template: require('./favorite-button.component.html')
})
export class FavoriteButtonComponent implements OnInit, OnDestroy {
    // Optional section override (auto-detected from state if not provided)
    @Input() section: string;
    @Input() showLabel: boolean = false;

    // Tracks if current page is bookmarked
    isFavorited: boolean = false;

    /** Last title emitted by GfTitleService — the primary source. */
    private currentTitle: string = '';

    private subscriptions: Subscription[] = [];

    constructor(
        private favoritesService: FavoritesService,
        private stateService: StateService,
        private titleService: GfTitleService,
        private notificationService: NotificationService,
        private pageContextService: PageContextService
    ) {}

    ngOnInit(): void {
        this.favoritesService.init().then(() => {
            this.checkIfBookmarked();
        });

        // Primary: track title from GfTitleService via NotificationService
        this.notificationService.subscribe(
            'FavoriteButtonComponent',
            GfTitleService.TITLE_CHANGED_EVENT,
            (title: string) => { this.currentTitle = title; }
        );

        // Subscribe to favorites changes to update state
        this.subscriptions.push(
            this.favoritesService.favoritesChanged$.subscribe(() => {
                this.checkIfBookmarked();
            })
        );

        // Re-check bookmarked state when page context changes (on navigation)
        this.subscriptions.push(
            this.pageContextService.contextChanged$.subscribe(() => {
                this.checkIfBookmarked();
            })
        );
    }

    ngOnDestroy(): void {
        this.notificationService.unsubscribe('FavoriteButtonComponent', GfTitleService.TITLE_CHANGED_EVENT);
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    /**
     * Get current URL from ui-router state
     */
    private getCurrentUrl(): string {
        try {
            const currentState = this.stateService.current;
            const params = this.stateService.params;
            if (currentState && currentState.name) {
                return this.stateService.href(currentState.name, params) || window.location.pathname;
            }
        } catch (e) {
            // Fall back to window.location.pathname
        }
        return window.location.pathname;
    }

    /**
     * Toggle bookmark state: add if not bookmarked, remove if bookmarked.
     *
     * Title resolution order:
     *   1. GfTitleService (last emitted title) — primary
     *   2. PageContextService.getStructuredTitle() — fallback when title is
     *      missing or doesn't follow the standard "Section - Subsection" format
     */
    toggleFavorite(): void {
        const currentUrl = this.getCurrentUrl();

        if (this.isFavorited) {
            this.favoritesService.removeFavoriteByUrl(currentUrl);
        } else {
            let title = this.currentTitle;

            // Fallback: if GfTitleService hasn't emitted a standard title,
            // let PageContextService build one from ui-router transition data.
            if (!title || !this.titleService.isStandardFormat(title)) {
                title = this.pageContextService.getStructuredTitle();
            }

            const section = this.section || this.pageContextService.getSectionType(this.stateService.current?.name);
            const bookmark = this.favoritesService.createBookmark(section, title, currentUrl);
            this.favoritesService.addFavorite(bookmark);
        }
    }

    /**
     * Check if current page URL exists in favorites list
     */
    private checkIfBookmarked(): void {
        const currentUrl = this.getCurrentUrl();
        this.isFavorited = this.favoritesService.isUrlBookmarked(currentUrl);
    }
}
